import { useCallback, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { MRT_ColumnDef } from "material-react-table"
import { Box, MenuItem } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { enqueueSnackbar } from "notistack"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { useApiQueryTable } from "../../../../../../core/api/components/ApiQueryTable"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { DeleteConfirmDialog } from "../../../../../../shared/components/dialogs/DeleteConfirmDialog"
import { StandardAlertInfo } from "../../../../../../shared/components/alert/StandardAlertInfo"
import { getEditBillReceiverRoute } from "../../../../../../constants/routes"

const useStyles = makeStyles()(() => ({
    infoAlert: {
        backgroundColor: "white",
    },
}))
import { fetchBillReceivers, deleteBillReceiver, FetchBillReceiversResponse, UserSettingsQuery } from "../settingsApi"
import { BillReceiver, BillReceiverEndpointType } from "../settingsTypes"
import { useAppSelector } from "../../../../../store/storeHooks"
import { selectEffectiveUserId } from "../../../../../context/contextSelectors"

const defaultDeleteModalState = {
    showConfirmModal: false,
    confirmModalText: "",
    itemId: null as number | null,
}

function transformData(data: FetchBillReceiversResponse) {
    return data.billReceivers
}

function getEndpointDisplay(endpoint: BillReceiver["endpoint"]): string {
    switch (endpoint.type) {
        case BillReceiverEndpointType.EAN:
            return endpoint.ean ? `EAN: ${endpoint.ean}` : "-"
        case BillReceiverEndpointType.EMAIL:
            return endpoint.email || "-"
        default:
            return "-"
    }
}

export function BillReceiversTab() {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const navigate = useNavigate()
    const userId = useAppSelector(selectEffectiveUserId) || 0

    const [deleteModalState, setDeleteModalState] = useState(defaultDeleteModalState)

    const billReceiversQuery = useApiQuery(fetchBillReceivers, {
        queryName: UserSettingsQuery.BILL_RECEIVERS,
        dependencies: {
            userId,
        },
        select: transformData,
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(billReceiversQuery)

    const { mutate: callDeleteBillReceiver } = useApiMutation(deleteBillReceiver, {
        async onSuccess(_, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [UserSettingsQuery.BILL_RECEIVERS] })
            enqueueSnackbar({
                variant: "success",
                message: data.serviceResponse.displayMessage,
            })
        },
    })

    const handleDeleteClick = useCallback(() => {
        if (deleteModalState.itemId != null) {
            callDeleteBillReceiver({ userId, billReceiverId: deleteModalState.itemId })
        }
    }, [deleteModalState.itemId, callDeleteBillReceiver, userId])

    const handleEdit = (billReceiver: BillReceiver) => {
        navigate(getEditBillReceiverRoute(billReceiver.id))
    }

    const columns = useMemo<MRT_ColumnDef<BillReceiver>[]>(
        () => [
            columnFactory.shortNameColumn({
                header: l10n.getString("user-settings-table-header-name"),
                accessorKey: "name",
            }),
            columnFactory.nameColumn({
                header: l10n.getString("user-settings-table-header-address"),
                accessorFn: (row) => row.address?.displayName || "-",
            }),
            columnFactory.nameColumn({
                header: l10n.getString("user-settings-table-header-endpoint"),
                accessorFn: (row) => getEndpointDisplay(row.endpoint),
            }),
            columnFactory.activeInactiveStatusColumn(
                {
                    header: l10n.getString("user-settings-table-header-status"),
                    accessorKey: "isActive",
                },
                (row) => row.isActive,
            ),
        ],
        [l10n, columnFactory],
    )

    return (
        <>
            <Box mb={3}>
                <LocalizedStrict id="user-settings-bill-receivers-info" attrs={{ title: true }}>
                    <StandardAlertInfo
                        title="Here are your personal bill receivers, which you can select when ordering meeting service. You have the option to create a new bill receiver during the ordering process, if the selected canteen allows it."
                        className={classes.infoAlert}
                    />
                </LocalizedStrict>
            </Box>
            <ApiQueryTable
                columns={columns}
                initialState={{ density: "comfortable" }}
                renderRowActionMenuItems={({ row, closeMenu }) => [
                    <MenuItem
                        onClick={() => {
                            handleEdit(row.original)
                            closeMenu()
                        }}
                        key={`edit-${row.original.id}`}
                    >
                        <LocalizedStrict id="user-settings-action-edit">
                            <>Edit</>
                        </LocalizedStrict>
                    </MenuItem>,
                    <MenuItem
                        onClick={() => {
                            setDeleteModalState({
                                showConfirmModal: true,
                                confirmModalText: row.original.name,
                                itemId: row.original.id,
                            })
                            closeMenu()
                        }}
                        key={`delete-${row.original.id}`}
                        sx={{ color: "error.main" }}
                    >
                        <LocalizedStrict id="user-settings-action-delete">
                            <>Delete</>
                        </LocalizedStrict>
                    </MenuItem>,
                ]}
            />
            {deleteModalState.showConfirmModal && (
                <LocalizedStrict id="user-settings-delete-bill-receiver-dialog" attrs={{ typeOfEntity: true }}>
                    <DeleteConfirmDialog
                        typeOfEntity="bill receiver"
                        entityName={deleteModalState.confirmModalText}
                        handleClick={handleDeleteClick}
                        open={deleteModalState.showConfirmModal}
                        onClose={() => setDeleteModalState(defaultDeleteModalState)}
                    />
                </LocalizedStrict>
            )}
        </>
    )
}
